import _cloneDeep from 'lodash/cloneDeep';
import _last from 'lodash/last';
import _mean from 'lodash/mean';
import { SMART_INVEST, BUILD_YOUR_OWN } from 'src/enums/planTypes';
import { property as propertyPropType } from 'scripts/constants/PropTypes';
import { todaysDateInSydney } from 'src/utils/time';
import Highchart from 'src/third-party/highchart/Highchart';
import Paragraph from 'src/design/styleguide/Paragraph';
import addAllDaysInBetweenDataPoints from 'src/components/portfolioWidget/portfolioOverTimeChart/utils/addAllDaysInBetweenDataPoints';
import { purple600 } from 'src/themes/brickx/colors';

export const colors = {
  salmon: {
    solid: '#fa8c8c',
    gradient: [
      [0, 'rgba(252,126,15,0.2)'],
      [1, 'rgba(252,126,15,0)'],
    ],
  },
  yolk: {
    solid: '#fac800',
    gradient: [
      [0, 'rgba(139,190,234,0.2)'],
      [1, 'rgba(139,190,234,0)'],
    ],
  },
};

const chartLineConfig = (color) => ({
  showInLegend: false,
  color: color.solid,
  lineWidth: 1,
  marker: {
    symbol: 'circle',
  },
});

const getPlanDisplayName = (planType) =>
  ({
    [SMART_INVEST]: 'Smart Invest',
    [BUILD_YOUR_OWN]: 'Build My Own',
  })[planType];

const getPlanColor = (planType) =>
  ({
    [SMART_INVEST]: colors.salmon,
    [BUILD_YOUR_OWN]: colors.yolk,
  })[planType];

const withTimestamps = (list) =>
  list.map(({ value, date }) => ({ value, date: new Date(date).valueOf() }));

export const getTotalAccountValueHistory = (plans) => {
  const histories = plans.map((plan) =>
    withTimestamps(plan.accountValueAcrossTime)
      .filter(
        (point) =>
          Number.isFinite(point.date) &&
          point.value !== null &&
          point.value !== '' &&
          Number.isFinite(Number(point.value)),
      )
      .sort((left, right) => left.date - right.date),
  );
  const dates = Array.from(
    new Set(
      histories.reduce(
        (all, points) => [...all, ...points.map((point) => point.date)],
        [],
      ),
    ),
  ).sort((left, right) => left - right);
  const positions = histories.map(() => 0);
  const balances = histories.map(() => 0);

  return dates.map((date) => {
    histories.forEach((points, planIndex) => {
      while (
        positions[planIndex] < points.length &&
        points[positions[planIndex]].date <= date
      ) {
        balances[planIndex] = Number(points[positions[planIndex]].value);
        positions[planIndex] += 1;
      }
    });
    return {
      date,
      value: balances.reduce((total, balance) => total + balance, 0),
    };
  });
};

export const getCompactAccountValueHistory = (plans) => {
  const history = getTotalAccountValueHistory(plans);
  if (history.length <= 1) return history;

  const first = history[0];
  const last = history[history.length - 1];
  const sampled = [first];
  const month = new Date(first.date);
  month.setUTCDate(1);
  month.setUTCHours(0, 0, 0, 0);
  month.setUTCMonth(month.getUTCMonth() + 1);
  let position = 0;
  while (month.getTime() < last.date) {
    const date = month.getTime();
    while (
      position + 1 < history.length &&
      history[position + 1].date <= date
    ) {
      position += 1;
    }
    sampled.push({ date, value: history[position].value });
    month.setUTCMonth(month.getUTCMonth() + 1);
  }
  sampled.push(last);
  return sampled;
};

export const getSmoothedAccountValueHistory = (history) => {
  const radius = Math.min(12, Math.max(1, Math.floor(history.length / 6)));
  return history.map((point, index) => {
    if (index === 0 || index === history.length - 1) {
      return { ...point, trendValue: point.value };
    }
    const start = Math.max(0, index - radius);
    const window = history.slice(start, index + radius + 1);
    const weights = window.map((neighbor, position) =>
      Math.exp(-0.5 * Math.pow((start + position - index) / (radius / 2), 2)),
    );
    const values = window.map(({ value }) => value);
    const weightedValues = values.map(
      (value, position) => value * weights[position],
    );
    const trendValue = Math.min(
      Math.max(_mean(weightedValues) / _mean(weights), Math.min(...values)),
      Math.max(...values),
    );
    return { ...point, trendValue };
  });
};

const makeTheLatestDayConsistent = (plans) => {
  plans.map((plan) => {
    const latestPoint = _last(plan.accountValueAcrossTime);
    const todaysDate = todaysDateInSydney();
    if (latestPoint && latestPoint.date !== todaysDate) {
      plan.accountValueAcrossTime.push({
        value: latestPoint.value,
        date: todaysDateInSydney(),
      });
    }
  });
  return plans;
};

const PortfolioNoHistoryPlaceholder = () => (
  <div data-test-reference="portfolio-no-history-placeholder">
    <img
      className="portfolio-widget__no-bricks-placeholder"
      src="/static/img/illustration-colored-computer-screen-with-charts.svg"
      alt="An illustration of a computer screen, featuring various charting graphics"
    />
    <Paragraph>
      After your first deposit, we’ll show your total portfolio value here.
    </Paragraph>
  </div>
);

const isDataAvailable = (accountOverTime) =>
  accountOverTime.some(
    ({ accountValueAcrossTime }) => accountValueAcrossTime.length,
  );

const PortfolioOverTimeChart = ({
  className,
  data,
  legendDisabled,
  compact,
}) => {
  if (!isDataAvailable(data)) return <PortfolioNoHistoryPlaceholder />;

  const equalizedLastDays = makeTheLatestDayConsistent(_cloneDeep(data));
  if (compact) {
    const compactHistory = getSmoothedAccountValueHistory(
      getCompactAccountValueHistory(equalizedLastDays),
    );
    return (
      <div
        className={className}
        data-test-reference="compact-account-value-chart"
        role="img"
        aria-label="Smoothed total account value over time"
      >
        <Highchart
          config={{
            chart: {
              type: 'areaspline',
              height: 168,
              backgroundColor: 'transparent',
              spacing: [8, 0, 4, 0],
              style: { fontFamily: 'inherit' },
            },
            title: false,
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: { type: 'datetime', visible: false },
            yAxis: { title: false, visible: false, min: null },
            plotOptions: {
              series: {
                animation: false,
                lineWidth: 2,
                marker: {
                  enabled: compactHistory.length === 1,
                  radius: 3,
                  states: { hover: { enabled: true, radius: 3 } },
                },
              },
            },
            tooltip: {
              split: false,
              xDateFormat: '%e %b %Y',
              headerFormat:
                '<span style="font-size:16px">{point.key}</span><br/>',
              pointFormat:
                'Total account value: <b>${point.accountValue:,.2f}</b><br/>Smoothed trend: <b>${point.y:,.2f}</b>',
              valuePrefix: '$',
              valueDecimals: 2,
              style: { fontSize: '16px' },
            },
            series: [
              {
                name: 'Account value trend',
                color: purple600,
                fillColor: {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                  stops: [
                    [0, 'rgba(136,90,221,0.24)'],
                    [1, 'rgba(136,90,221,0)'],
                  ],
                },
                data: compactHistory.map(({ date, value, trendValue }) => ({
                  x: date,
                  y: trendValue,
                  accountValue: value,
                })),
              },
            ],
          }}
        />
      </div>
    );
  }
  const series = equalizedLastDays.map((plan) => {
    const planType = plan.planSubscription.planType;
    return {
      ...chartLineConfig(getPlanColor(planType)),
      showInLegend: true,
      name: getPlanDisplayName(planType),
      data: addAllDaysInBetweenDataPoints(
        withTimestamps(plan.accountValueAcrossTime),
      ).map(({ value, date }) => [date, value]),
    };
  });

  const moreThanOnePlan = series.length > 1;

  const legendEnabled = moreThanOnePlan && !legendDisabled;

  return (
    <div className={className} data-test-reference="portfolio-chart">
      <Highchart
        data-test-reference="historical-portfolio-chart"
        config={{
          chart: {
            type: 'areaspline',
            backgroundColor: '#FFFFFF',
          },
          plotOptions: {
            series: {
              stacking: 'normal',
            },
          },
          xAxis: {
            type: 'datetime',
          },
          yAxis: {
            title: false,
            labels: { format: '${value:,.0f}' },
          },
          title: false,
          credits: {
            enabled: false,
          },
          loading: false,
          legend: {
            enabled: legendEnabled,
            verticalAlign: 'top',
            align: 'right',
          },
          tooltip: {
            split: true,
            valuePrefix: '$',
            valueDecimals: 2,
          },
          series: series,
        }}
      />
    </div>
  );
};

PortfolioOverTimeChart.propTypes = {
  property: propertyPropType,
};

export default PortfolioOverTimeChart;
